import { User, UserManager } from 'oidc-client';
import React, { useEffect, useRef, ReactElement } from 'react';
import { authActions } from '../reducers/authReducer/authSlice';
import jwt_decode from 'jwt-decode';
import { signoutAsync } from '../pages/SignoutOidc/SignoutOidc.component';
import { getUserManager } from '../services/users/identity/user.service';

interface AssessmentSessionTokenInfo {
  account_type?: 'examiner' | 'administrator';
  assessmentSessionId?: string | number;
  isChairman?: boolean | number;
}
export default function AuthProvider({ userManager: manager, store, children }: any): ReactElement {
  const userManager = useRef<UserManager>();

  useEffect(() => {
    userManager.current = manager;
    const onUserLoaded = (user: User) => {
      const date = new Date();
      store.dispatch(authActions.storeUserAndSetAuthHeader(user));
      console.log('Token generated ' + date.toISOString());
    };

    const onUserUnloaded = () => {
      console.log(`user unloaded`);
    };

    const storeUser = async (assessmentSessionId: string | number | null, asExaminer: boolean) => {

      const currentUserManager = getUserManager();

      const silentSignInArgs = assessmentSessionId !== null
        ? { assessmentSessionId, asExaminer }
        : {};

      const newUser = await currentUserManager.signinSilent(silentSignInArgs);

      currentUserManager.storeUser(newUser);
      return authActions.storeUserAndSetAuthHeader(newUser);
    }

    const onAccessTokenExpiring = async () => {
      try {
        const currentUserManager = getUserManager();
        const user = await currentUserManager.getUser();

        if (user !== null) {
          console.log('Access token expiring, renewing...');

          const context = sessionStorage.getItem('context');
          if (context === 'admin') {
            const action = await storeUser(null, false);
            store.dispatch(action);
            return;
          }

          try {
            const jwt: AssessmentSessionTokenInfo = jwt_decode(user.access_token);

            if (jwt?.assessmentSessionId != null && jwt.assessmentSessionId !== undefined) {
              const asExaminer = jwt.isChairman === true || jwt.isChairman === 1;
              const action = await storeUser(jwt.assessmentSessionId, asExaminer);
              store.dispatch(action);
              return;
            }

            if (jwt?.account_type === 'examiner') {
              const action = await storeUser(null, false);
              store.dispatch(action);
              return;
            }

            console.warn('No assessmentSessionId in token, cannot refresh. Token will expire.');
            const action = await storeUser(null, false);
            store.dispatch(action);
            return;
          } catch (decodeError) {
            console.error('Failed to decode JWT token:', decodeError);
            console.warn('Cannot decode token, renewal aborted. Token will expire.');
            const action = await storeUser(null, false);
            store.dispatch(action);
            return;
          }
        }
        const date = new Date();
        console.log('user token expiring but no user found ' + date.toISOString());
      }
      catch (e) {
        console.error(`Error during token renewal: ${e}`);
        authActions.storeUserError();
      }
    };

    const onAccessTokenExpired = () => {
      const date = new Date();
      console.log('user token expired ' + date.toISOString());

      const currentUserManager = getUserManager();
      let isProcessing = false;

      currentUserManager.getUser().then((user) => {
        // Prevent multiple calls to signout
        if (isProcessing) {
          console.log('Token expiry already being processed, ignoring duplicate call');
          return;
        }

        if (user && !user.expired) {
          console.log('Token was refreshed, user still valid - ignoring expired event');
          return;
        }

        isProcessing = true;
        console.log('No valid user found, proceeding with signout');
        
        // Add a small delay to prevent rapid repeated calls to signout
        setTimeout(() => {
          signoutAsync(false);
        }, 500);
      }).catch((error) => {
        if (isProcessing) {
          console.log('Token expiry already being processed, ignoring error');
          return;
        }

        isProcessing = true;
        console.error('Error checking user on token expiry:', error);
        
        // Add a small delay to prevent rapid repeated calls to signout
        setTimeout(() => {
          signoutAsync(false);
        }, 500);
      });
    };

    const onUserSignedOut = () => {
      signoutAsync(false);
    };

    // events for user
    //@ts-ignore
    userManager.current.events.addUserLoaded(onUserLoaded);
    //@ts-ignore
    userManager.current.events.addUserUnloaded(onUserUnloaded);
    //@ts-ignore
    userManager.current.events.addAccessTokenExpiring(onAccessTokenExpiring);
    //@ts-ignore
    userManager.current.events.addAccessTokenExpired(onAccessTokenExpired);
    //@ts-ignore
    userManager.current.events.addUserSignedOut(onUserSignedOut);

    // Specify how to clean up after this effect:
    return function cleanup() {
      //@ts-ignore
      userManager.current.events.removeUserLoaded(onUserLoaded);
      //@ts-ignore
      userManager.current.events.removeUserUnloaded(onUserUnloaded);
      //@ts-ignore
      userManager.current.events.removeAccessTokenExpiring(onAccessTokenExpiring);
      //@ts-ignore
      userManager.current.events.removeAccessTokenExpired(onAccessTokenExpired);
      //@ts-ignore
      userManager.current.events.removeUserSignedOut(onUserSignedOut);
    };
  }, [manager, store]);

  return React.Children.only(children);
}


